import { computed, Injectable, signal } from '@angular/core';

import { TellentRoute } from '@core/tellent-types';

/**
 * @edsc
 * The purpose of this service is to handle navigation within the Tellent Client compatible application.
 * It provides a way to register navigation routes, handle navigation events, and manage the current location.
 * It's exposed publicly so that host applications can register their own (recognized) navigation routes and handle navigation events.
 */
@Injectable({
  providedIn: 'root',
})
export class TellentNavigationRoutingService {
  public location = signal<string>('');
  public recognizedRoutes = signal<TellentRoute[]>([]);

  public possibleMatch = computed(() => {
    const routes = this.recognizedRoutes();
    const location = this.location();
    return routes.find(route => route.matchPattern(location));
  });

  private linkHandler: (url: string) => void = this.noopNavLinkHandler;

  /**
   * @desc
   * Delegates navigation to a custom handler.
   * Host app should register a handler to handle navigation.
   * Handler is called with the URL to navigate to, when `navigate` method is called.
   * Currently used by `TellentNavigationComponent` to notify the host app about navigation events.
   * @param handler
   */
  public registerNavHandler(handler: (url: string) => void): void {
    this.linkHandler = handler;
  }

  /**
   * @desc
   * Navigates to the specified URL using the registered navigation handler.
   * @param url
   */
  public navigate(url: string) {
    this.linkHandler(url);
  }

  private noopNavLinkHandler() {
    console.warn(
      `[Tellent Client] Noop redirect handler called, register custom handler with "registerHandler" method.`,
    );
  }
}
